import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CameraIcon,
  ArrowPathIcon,
  FilmIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const MAX_CANVAS_WIDTH = 640;

const VideoFrameCapture = ({ videoFile, onCapture, capturedUrl, disabled }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDims, setVideoDims] = useState({ w: 640, h: 360 });
  const [thumbnailStrip, setThumbnailStrip] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const autoCapturedRef = useRef(false);

  // Create/revoke object URL
  useEffect(() => {
    if (!videoFile) return;
    const url = URL.createObjectURL(videoFile);
    setVideoUrl(url);
    setThumbnailStrip([]);
    setPreviewReady(false);
    setCurrentTime(0);
    autoCapturedRef.current = false; // allow one auto-capture per new video
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  // Generate thumbnail strip once video metadata is loaded
  const generateStrip = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.duration || video.duration === Infinity) return;

    setGenerating(true);
    const strip = [];
    const thumbW = 96;
    const thumbH = Math.round(thumbW * (video.videoHeight / video.videoWidth)) || 54;
    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = thumbW;
    thumbCanvas.height = thumbH;
    const thumbCtx = thumbCanvas.getContext('2d');

    for (let i = 1; i <= 9; i++) {
      const time = (video.duration * i) / 10;
      video.currentTime = time;
      await new Promise((resolve) => {
        const onSeeked = () => { video.removeEventListener('seeked', onSeeked); resolve(); };
        video.addEventListener('seeked', onSeeked);
      });
      thumbCtx.drawImage(video, 0, 0, thumbW, thumbH);
      strip.push({ time, dataUrl: thumbCanvas.toDataURL('image/jpeg', 0.6) });
    }

    setThumbnailStrip(strip);
    // Seek to first strip frame as default
    video.currentTime = strip[0]?.time || 0;
    setGenerating(false);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration);
    const scale = video.videoWidth > MAX_CANVAS_WIDTH ? MAX_CANVAS_WIDTH / video.videoWidth : 1;
    setVideoDims({
      w: Math.round(video.videoWidth * scale),
      h: Math.round(video.videoHeight * scale),
    });
    generateStrip();
  }, [generateStrip]);

  const handleSeeked = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setPreviewReady(true);
  }, []);

  const handleScrub = useCallback((e) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (videoRef.current) videoRef.current.currentTime = time;
  }, []);

  const handleStripClick = useCallback((time) => {
    setCurrentTime(time);
    if (videoRef.current) videoRef.current.currentTime = time;
  }, []);

  const handleCapture = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(
      (blob) => { if (blob && onCapture) onCapture(blob); },
      'image/jpeg',
      0.85
    );
  }, [onCapture]);

  // Auto-capture a default frame once the preview is ready, so every video
  // gets a thumbnail even if the user never clicks capture. They can still
  // scrub and recapture a different frame.
  useEffect(() => {
    if (generating || !previewReady || autoCapturedRef.current || !onCapture) return;
    autoCapturedRef.current = true;
    const t = setTimeout(() => handleCapture(), 150); // let the parked frame paint
    return () => clearTimeout(t);
  }, [generating, previewReady, onCapture, handleCapture]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!videoFile || !videoUrl) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      <label className="label flex items-center gap-1.5">
        <FilmIcon className="w-4 h-4" />
        Thumbnail — Capture from Video
      </label>

      {/* Hidden video element for frame extraction */}
      <video
        ref={videoRef}
        src={videoUrl}
        onLoadedMetadata={handleLoadedMetadata}
        onSeeked={handleSeeked}
        preload="metadata"
        muted
        playsInline
        crossOrigin="anonymous"
        className="hidden"
      />

      {/* Preview canvas */}
      <div className="relative rounded-xl overflow-hidden bg-gray-900 border border-gray-200 dark:border-dark-700">
        <canvas
          ref={canvasRef}
          width={videoDims.w}
          height={videoDims.h}
          className="w-full"
          style={{ aspectRatio: `${videoDims.w} / ${videoDims.h}` }}
        />
        {!previewReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
            <div className="text-center">
              <FilmIcon className="w-8 h-8 text-gray-500 mx-auto mb-2 animate-pulse" />
              <p className="text-xs text-gray-400">Loading video frames...</p>
            </div>
          </div>
        )}

        {/* Timestamp badge */}
        {previewReady && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        )}
      </div>

      {/* Timeline scrubber */}
      {duration > 0 && (
        <div className="px-1">
          <input
            type="range"
            min={0}
            max={duration}
            step={0.1}
            value={currentTime}
            onChange={handleScrub}
            disabled={disabled || generating}
            className="w-full h-1.5 bg-gray-200 dark:bg-dark-700 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
              [&::-webkit-slider-thumb]:bg-primary-500 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-md
              [&::-webkit-slider-thumb]:hover:bg-primary-400 [&::-webkit-slider-thumb]:transition-colors"
          />
        </div>
      )}

      {/* Thumbnail strip — quick frame selection */}
      {thumbnailStrip.length > 0 && (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {thumbnailStrip.map((thumb, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleStripClick(thumb.time)}
              disabled={disabled}
              className={`flex-shrink-0 rounded-md overflow-hidden border-2 transition-all hover:scale-105
                ${Math.abs(currentTime - thumb.time) < 0.5
                  ? 'border-primary-500 shadow-md shadow-primary-500/20'
                  : 'border-transparent opacity-70 hover:opacity-100'
                }`}
            >
              <img src={thumb.dataUrl} alt={`Frame at ${formatTime(thumb.time)}`} className="w-16 h-9 object-cover" />
            </button>
          ))}
        </div>
      )}

      {generating && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
          Generating preview frames...
        </div>
      )}

      {/* Capture button + preview */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleCapture}
          disabled={disabled || !previewReady}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
            bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed
            transition-colors shadow-sm"
        >
          <CameraIcon className="w-4 h-4" />
          {capturedUrl ? 'Recapture Frame' : 'Capture This Frame'}
        </button>

        {capturedUrl && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2"
          >
            <img
              src={capturedUrl}
              alt="Captured thumbnail"
              className="w-16 h-9 rounded-md object-cover border border-gray-200 dark:border-dark-600"
            />
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <CheckCircleIcon className="w-3.5 h-3.5" />
              Captured
            </span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default VideoFrameCapture;
